import { Vehicle } from '@/types';
import { VehicleModel } from '@/models/Vehicle';

/**
 * Parser for Lua vehicle files
 */
export class LuaVehicleParser {
  /**
   * Parse vehicle file content and extract vehicle data
   */
  parseVehicleFile(luaContent: string): VehicleModel[] {
    try {
      const vehicles = this.extractVehicleData(luaContent);
      return vehicles.map(vehicleData => new VehicleModel({
        ...vehicleData,
        source: 'lua'
      })).filter(vehicle => vehicle.isValid());
    } catch (error) {
      throw new Error(`Failed to parse Lua file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Extract vehicle data from Lua content using regex
   */
  private extractVehicleData(luaContent: string): Partial<Vehicle>[] {
    const vehicles: Partial<Vehicle>[] = [];
    
    // Remove comments but preserve structure
    const cleanContent = luaContent.replace(/---.*$/gm, '').replace(/--.*$/gm, '');
    
    // Find the return table - more flexible pattern
    const returnMatch = cleanContent.match(/return\s*{([\s\S]*?)}\s*$/);
    if (!returnMatch) {
      throw new Error('No return table found in Lua file');
    }

    const tableContent = returnMatch[1];
    
    // Extract individual vehicle entries using brace counting to handle nested objects
    const vehicles_extracted = this.extractVehicleEntries(tableContent);
    
    for (const { key: vehicleKey, content: vehicleContent } of vehicles_extracted) {
      
      try {
        const vehicleData = this.parseVehicleProperties(vehicleContent);
        vehicleData.key = vehicleKey;
        
        // Ensure model is set (use key if model is missing)
        if (!vehicleData.model) {
          vehicleData.model = vehicleKey;
        }
        
        vehicles.push(vehicleData);
      } catch (error) {
        console.warn(`Failed to parse vehicle ${vehicleKey}:`, error);
      }
    }
    
    return vehicles;
  }

  /**
   * Extract vehicle entries from table content using brace counting to handle nested objects
   */
  private extractVehicleEntries(tableContent: string): Array<{ key: string; content: string }> {
    const entries: Array<{ key: string; content: string }> = [];
    const lines = tableContent.split('\n');
    let currentEntry: { key: string; content: string; braceCount: number } | null = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines and comments
      if (!line || line.startsWith('--')) continue;
      
      // Check if this line starts a new vehicle entry
      const entryMatch = line.match(/^(\w+)\s*=\s*\{/);
      if (entryMatch) {
        // If we have a current entry, save it
        if (currentEntry) {
          entries.push({ key: currentEntry.key, content: currentEntry.content });
        }
        
        // Start new entry
        currentEntry = {
          key: entryMatch[1],
          content: '',
          braceCount: 1 // We found the opening brace
        };
        
        // Add the rest of the line after the opening brace
        const afterBrace = line.substring(entryMatch[0].length);
        if (afterBrace.trim()) {
          currentEntry.content += afterBrace + '\n';
          // Count braces in the rest of the line
          for (const char of afterBrace) {
            if (char === '{') currentEntry.braceCount++;
            else if (char === '}') currentEntry.braceCount--;
          }
        }
        continue;
      }
      
      // If we're inside an entry, add the line and count braces
      if (currentEntry) {
        currentEntry.content += line + '\n';
        
        // Count braces in this line
        for (const char of line) {
          if (char === '{') {
            currentEntry.braceCount++;
          } else if (char === '}') {
            currentEntry.braceCount--;
            
            // If brace count reaches 0, we've found the end of this entry
            if (currentEntry.braceCount === 0) {
              entries.push({ key: currentEntry.key, content: currentEntry.content });
              currentEntry = null;
              break;
            }
          }
        }
      }
    }
    
    // Handle case where file ends without closing brace
    if (currentEntry && currentEntry.content.trim()) {
      entries.push({ key: currentEntry.key, content: currentEntry.content });
    }
    
    return entries;
  }

  /**
   * Parse individual vehicle properties
   */
  private parseVehicleProperties(content: string): Partial<Vehicle> & { key?: string } {
    const vehicle: Partial<Vehicle> & { key?: string } = {};
    
    // Property patterns
    const patterns = {
      name: /name\s*=\s*['"]([^'"]+)['"]/,
      brand: /brand\s*=\s*['"]([^'"]+)['"]/,
      model: /model\s*=\s*['"]([^'"]+)['"]/,
      price: /price\s*=\s*(\d+)/,
      category: /category\s*=\s*['"]([^'"]+)['"]/,
      type: /type\s*=\s*['"]([^'"]+)['"]/,
      hash: /hash\s*=\s*`([^`]+)`/
    };

    // For each property, find all matches and use the one that's not inside a nested object
    for (const [key, pattern] of Object.entries(patterns)) {
      // Find all matches
      const matches = [...content.matchAll(new RegExp(pattern.source, 'g'))];
      
      if (matches.length > 0) {
        // For each match, check if it's inside a nested object
        for (let i = matches.length - 1; i >= 0; i--) {
          const match = matches[i];
          const matchIndex = match.index || 0;
          
          // Check if this match is inside a nested object
          // We do this by counting braces before the match
          const beforeMatch = content.substring(0, matchIndex);
          let braceCount = 0;
          
          // Simple brace counting to detect nested objects
          for (let j = 0; j < beforeMatch.length; j++) {
            if (beforeMatch[j] === '{') {
              braceCount++;
            } else if (beforeMatch[j] === '}') {
              braceCount--;
            }
          }
          
          // If we're not in a nested object, or we're at the top level, use this match
          if (braceCount <= 1) {
            if (key === 'price') {
              (vehicle as Record<string, unknown>)[key] = parseInt(match[1]);
            } else {
              (vehicle as Record<string, unknown>)[key] = match[1];
            }
            break; // Use the first valid match from the end
          }
        }
      }
    }
    
    return vehicle;
  }

  /**
   * Validate vehicle structure
   */
  validateVehicleStructure(vehicleData: Partial<Vehicle>): boolean {
    const required = ['model'];
    const missing = required.filter(field => !vehicleData[field as keyof Vehicle]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    return true;
  }
}